import classnames from 'classnames'
import { useMemo } from 'react'

import { IconChevronLeft, IconChevronRight } from '@/components'
import { FormChangeEvent } from '@/types'

type DatePickerHeadingProps = {
    id?: string
    currentDate: Date
    'data-test'?: string
    maxDate?: Date
    minDate?: Date
    onChange: (date: Date) => void
}

export const DatePickerHeading = ({
    id,
    currentDate,
    maxDate,
    minDate,
    'data-test': dataTest,
    ...props
}: DatePickerHeadingProps) => {
    const weeks = useMemo(() => {
        const start = new Date().startOf('week')
        const arr: Date[] = []
        do {
            arr.push(start.clone())
            start.add('day', 1)
        } while (start.isBefore(new Date().endOf('week')))
        return arr
    }, [])

    const months = useMemo(() => {
        const start = currentDate.clone()
            .startOf('year')
        const end = currentDate.clone()
            .endOf('year')
        const arr: Date[] = []
        do {
            arr.push(start.clone())
            start.add('month', 1)
        } while (start.isBefore(end))
        return arr
    }, [currentDate])

    const years = useMemo(() => {
        const start = currentDate.clone().startOf('month').subtract('years', 20)
        const end = currentDate.clone().startOf('month').add('years', 20)
        const arr: Date[] = []
        do {
            arr.push(start.clone())
            start.add('year', 1)
        } while (start.isBefore(end))
        return arr
    }, [currentDate])

    const handleChange = (e: FormChangeEvent) => {
        props.onChange(new Date(e.target.value as string))
    }

    return <>
        <div
            className="flex"
            data-test={`${dataTest}${dataTest ? '-' : ''}header`}
        >
            <button
                className={classnames('w-10 h-10 flex justify-center items-center',
                    currentDate.clone().subtract('month', 1).isBefore(minDate?.startOf('month'))
                        ? 'stroke-gray-300' : 'cursor-pointer stroke-gray-600')}
                disabled={currentDate.clone().subtract('month', 1).isBefore(minDate?.startOf('month'))}
                onClick={() => {
                    props.onChange(currentDate.clone()
                        .subtract('month', 1))
                }}
                data-test={`${dataTest}${dataTest ? '-' : ''}header-button-previous-month`}
            >
                <IconChevronLeft/>
            </button>
            <div className="grid grid-cols-2 grow gap-1 justify-center">
                <select
                    name="month"
                    id={`${id}-datepicker-month`}
                    value={currentDate.clone()
                        .startOf('month')
                        .toISODate()}
                    className="appearance-none text-right outline-0 [text-align-last:end]"
                    onChange={handleChange}
                    data-test={`${dataTest}${dataTest ? '-' : ''}header-month-select`}
                >
                    {months.map(item => <option
                        key={item.toISODate()}
                        value={item.toISODate()}
                        disabled={
                            item.isBefore(minDate?.startOf('month')) ||
                            item.isAfter(maxDate?.endOf('month'))
                        }
                        data-test={`${dataTest}${dataTest ? '-' : ''}header-month-select-option-${item.format({
                            month: 'long',
                            year: null,
                            day: null
                        }).toLowerCase()}`}
                    >
                        {item.format({
                            month: 'long',
                            day: null,
                            year: null
                        })}
                    </option>)}
                </select>
                <select
                    name="year"
                    id={`${id}-datepicker-year`}
                    value={currentDate.clone()
                        .startOf('month')
                        .toISODate()}
                    className="appearance-none outline-0"
                    onChange={handleChange}
                    data-test={`${dataTest}${dataTest ? '-' : ''}header-year-select`}
                >
                    {years.map(item => <option
                        key={item.toISODate()}
                        value={item.toISODate()}
                        disabled={
                            item.isBefore(minDate?.startOf('year')) ||
                            item.isAfter(maxDate?.endOf('year'))
                        }
                        data-test={`${dataTest}${dataTest ? '-' : ''}header-year-select-option-${item.getFullYear()}`}
                    >
                        {item.getFullYear()}
                    </option>)}
                </select>
            </div>
            <button
                className={classnames('w-10 h-10 flex justify-center items-center',
                    currentDate.clone().add('month', 1).isAfter(maxDate?.endOf('month'))
                        ? 'stroke-gray-300' : 'cursor-pointer stroke-gray-600')}
                disabled={currentDate.clone().add('month', 1).isAfter(maxDate?.endOf('month'))}
                onClick={() => {
                    props.onChange(currentDate.clone().add('month', 1))
                }}
                data-test={`${dataTest}${dataTest ? '-' : ''}header-button-next-month`}
            >
                <IconChevronRight/>
            </button>
        </div>
        <div
            className="flex"
            data-test={`${dataTest}${dataTest ? '-' : ''}header-weekdays`}
        >
            {weeks.map(item => <div
                key={item.toISODate()}
                className="w-10 h-10 flex justify-center items-center"
                data-test={`${dataTest}${dataTest ? '-' : ''}header-weekdays-${item.format({
                    weekday: 'long',
                    day: null,
                    month: null,
                    year: null
                }).toLowerCase()}`}
            >
                {item.format({
                    weekday: 'short',
                    day: null,
                    month: null,
                    year: null
                })
                    .slice(0, 2)}
            </div>)}
        </div>
    </>
}
